import { getAuthToken } from './auth/tokenStorage';
import AsyncStorage from '@react-native-async-storage/async-storage';

import { API_URL } from '../config';

import { createLogger } from '../utils/logger';

const logger = createLogger('caregiver');
export interface CaregiverInfo {
  id: string;
  userId: string;
  taskId: number;
  visibilityState: 'LOCKED' | 'UNLOCKED';
  fullName: string;
  phoneNumber: string;
  email: string;
  relationship: string;
  state: string;
  notes: string;
  additionalData?: any;
  createdAt: string;
  updatedAt: string;
}

export interface CreateCaregiverInfoData {
  taskId: number;
  fullName: string;
  phoneNumber: string;
  email: string;
  relationship: string;
  state: string;
  notes: string;
  additionalData?: any;
}

export interface UpdateCaregiverInfoData extends Partial<CreateCaregiverInfoData> {}

class CaregiverInfoService {
  private async getAuthToken(): Promise<string | null> {
    return await getAuthToken();
  }

  private async makeRequest<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    const token = await this.getAuthToken();
    
    const response = await fetch(`${API_URL}${endpoint}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...(token && { 'Authorization': `Bearer ${token}` }),
        ...options.headers,
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `HTTP ${response.status}: ${response.statusText}`);
    }

    return await response.json();
  }

  async getCaregiverInfoByTaskId(taskId: number, targetUserId?: string): Promise<CaregiverInfo[]> {
    logger.debug(`=== CAREGIVER SERVICE: Getting caregiver info for task ${taskId} ===`);
    const params = new URLSearchParams({ taskId: taskId.toString() });
    if (targetUserId) {
      params.append('targetUserId', targetUserId);
    }
    const response = await this.makeRequest<CaregiverInfo[]>(`/api/caregiver-info?${params.toString()}`);
    logger.debug(`=== CAREGIVER SERVICE: Retrieved ${response?.length || 0} caregiver records ===`);
    return response || [];
  }

  async getCaregiverInfo(id: string): Promise<CaregiverInfo> {
    logger.debug(`=== CAREGIVER SERVICE: Getting caregiver info ${id} ===`);
    const response = await this.makeRequest<CaregiverInfo>(`/api/caregiver-info/${id}`);
    logger.debug(`=== CAREGIVER SERVICE: Retrieved caregiver info ${id} ===`);
    return response;
  }

  async createCaregiverInfo(data: CreateCaregiverInfoData): Promise<CaregiverInfo> {
    logger.debug(`=== CAREGIVER SERVICE: Creating caregiver info ===`, data);
    const response = await this.makeRequest<CaregiverInfo>('/api/caregiver-info', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    logger.debug(`=== CAREGIVER SERVICE: Created caregiver info with ID ${response.id} ===`);
    return response;
  }

  async updateCaregiverInfo(id: string, data: UpdateCaregiverInfoData): Promise<CaregiverInfo> {
    logger.debug(`=== CAREGIVER SERVICE: Updating caregiver info ${id} ===`, data);
    const response = await this.makeRequest<CaregiverInfo>(`/api/caregiver-info/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
    logger.debug(`=== CAREGIVER SERVICE: Updated caregiver info ${id} ===`);
    return response;
  }

  async deleteCaregiverInfo(id: string): Promise<void> {
    logger.debug(`=== CAREGIVER SERVICE: Deleting caregiver info ${id} ===`);
    await this.makeRequest<void>(`/api/caregiver-info/${id}`, {
      method: 'DELETE',
    });
    logger.debug(`=== CAREGIVER SERVICE: Deleted caregiver info ${id} ===`);
  }

  async markAsCompleted(taskId: number): Promise<boolean> {
    const response = await this.makeRequest<boolean>(`/api/caregiver-info/complete/${taskId}`, {
      method: 'POST',
    });
    
    return response;
  }

  async setVisibilityState(id: string, visibilityState: 'LOCKED' | 'UNLOCKED'): Promise<CaregiverInfo> {
    logger.debug(`=== CAREGIVER SERVICE: Setting visibility ${visibilityState} for ${id} ===`);
    const response = await this.makeRequest<CaregiverInfo>(`/api/caregiver-info/${id}/visibility`, {
      method: 'PATCH',
      body: JSON.stringify({ visibilityState })
    });
    return response;
  }

  async lockCaregiverInfo(id: string): Promise<void> {
    logger.debug(`=== CAREGIVER SERVICE: Locking caregiver ${id} ===`);
    await this.makeRequest<void>(`/api/caregiver-info/${id}/lock`, {
      method: 'POST',
    });
    logger.debug(`=== CAREGIVER SERVICE: Successfully locked caregiver ${id} ===`);
  }

  async unlockCaregiverInfo(id: string, selectedEmails: string[]): Promise<void> {
    logger.debug(`=== CAREGIVER SERVICE: Unlocking caregiver ${id} for emails:`, selectedEmails);
    await this.makeRequest<void>(`/api/caregiver-info/${id}/unlock`, {
      method: 'POST',
      body: JSON.stringify({ selectedEmails }),
    });
    logger.debug(`=== CAREGIVER SERVICE: Successfully unlocked caregiver ${id} ===`);
  }

  async getCaregiverAccessGrants(id: string): Promise<any[]> {
    logger.debug(`=== CAREGIVER SERVICE: Getting access grants for caregiver ${id} ===`);
    const response = await this.makeRequest<{ success: boolean; data: any[] }>(`/api/caregiver-info/${id}/access-grants`);
    logger.debug(`=== CAREGIVER SERVICE: Retrieved access grants for caregiver ${id} ===`);
    return response?.data || [];
  }
}

export const caregiverInfoService = new CaregiverInfoService();